import React, { useState, useEffect } from 'react';
import * as LuIcons from 'react-icons/lu';
import SafeIcon from '../common/SafeIcon';

const { LuTag, LuClock, LuRefreshCw, LuFlame, LuZap } = LuIcons;

const pad = (n) => String(n).padStart(2, '0');

const getTimeLeft = (endDate) => {
  const diff = new Date(endDate) - new Date();
  if (diff <= 0) return null;
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.floor((diff % (1000 * 60)) / 1000);
  return { days, hours, minutes, seconds };
};

const computeEffectiveEnd = (car) => {
  if (!car.discount_end_date) return null;
  let end = new Date(car.discount_end_date);
  const now = new Date();
  if (car.discount_auto_repeat && car.discount_repeat_days > 0) {
    while (end <= now) {
      end = new Date(end.getTime() + car.discount_repeat_days * 24 * 60 * 60 * 1000);
    }
  }
  return end;
};

const TimeBlock = ({ value, label, pulse }) => (
  <div className="flex flex-col items-center gap-1">
    <div className={`relative bg-gradient-to-b from-slate-800 to-slate-900 text-white font-black text-xl leading-none rounded-xl min-w-[48px] h-[52px] flex items-center justify-center shadow-lg overflow-hidden ${pulse ? 'animate-pulse' : ''}`}>
      <div className="absolute inset-x-0 top-0 h-1/2 bg-white/5 rounded-t-xl" />
      <span className="tabular-nums relative z-10">{pad(value)}</span>
    </div>
    <span className="text-red-400 text-[10px] font-black uppercase tracking-wider">{label}</span>
  </div>
);

const Colon = () => (
  <div className="flex flex-col items-center justify-center gap-1.5 pb-5">
    <span className="w-1.5 h-1.5 bg-red-500 rounded-full animate-pulse" />
    <span className="w-1.5 h-1.5 bg-red-500 rounded-full animate-pulse" style={{ animationDelay: '0.5s' }} />
  </div>
);

const CompactTimeBlock = ({ value, label }) => (
  <div className="flex flex-col items-center">
    <div className="bg-white/20 backdrop-blur-sm text-white font-black text-sm leading-none px-2 py-1.5 rounded-lg min-w-[32px] text-center tabular-nums border border-white/30">
      {pad(value)}
    </div>
    <span className="text-white/70 text-[9px] font-bold mt-0.5">{label}</span>
  </div>
);

const DiscountTimer = ({ car, compact = false }) => {
  const [timeLeft, setTimeLeft] = useState(null);
  const [expired, setExpired] = useState(false);

  useEffect(() => {
    if (!car.discount_enabled || !car.discount_end_date) return;
    const update = () => {
      const effectiveEnd = computeEffectiveEnd(car);
      if (!effectiveEnd) { setExpired(true); return; }
      const left = getTimeLeft(effectiveEnd);
      if (!left) { setExpired(true); return; }
      setTimeLeft(left);
      setExpired(false);
    };
    update();
    const interval = setInterval(update, 1000);
    return () => clearInterval(interval);
  }, [car]);

  if (!car.discount_enabled || !car.discount_price || car.discount_price <= 0) return null;
  if (expired && !car.discount_auto_repeat) return null;

  const originalPrice = Number(car.total_price) > 0
    ? Number(car.total_price)
    : (Number(car.price) || 0) + (Number(car.customs_price) || 0) + (Number(car.shipping_price) || 0);
  const discountPrice = Number(car.discount_price);
  const savings = originalPrice - discountPrice;
  const pct = originalPrice > 0 ? Math.round((savings / originalPrice) * 100) : 0;
  const isUrgent = timeLeft && timeLeft.days === 0 && timeLeft.hours === 0;

  if (compact) {
    return (
      <div className={`rounded-xl overflow-hidden ${isUrgent ? 'bg-gradient-to-br from-red-600 to-rose-700' : 'bg-gradient-to-br from-red-500 to-rose-600'}`}>
        <div className="flex items-center justify-between px-3 py-2">
          <div className="flex items-center gap-1.5 text-white">
            <SafeIcon icon={isUrgent ? LuFlame : LuTag} className={`w-3.5 h-3.5 ${isUrgent ? 'animate-bounce' : ''}`} />
            <span className="text-xs font-black">تخفيض {pct}%</span>
          </div>
          <div className="text-right text-white">
            <div className="text-[10px] line-through opacity-60">{originalPrice.toLocaleString()} دج</div>
            <div className="font-black text-sm leading-tight">{discountPrice.toLocaleString()} دج</div>
          </div>
        </div>
        {timeLeft && (
          <div className="bg-black/30 px-3 py-2">
            <div className="flex items-center gap-2">
              <SafeIcon icon={LuClock} className="w-3 h-3 text-white/70 flex-shrink-0" />
              <span className="text-white/70 text-[10px] font-bold flex-shrink-0">ينتهي خلال</span>
              <div className="flex items-end gap-1 ms-auto" dir="ltr">
                {timeLeft.days > 0 && (
                  <>
                    <CompactTimeBlock value={timeLeft.days} label="يوم" />
                    <span className="text-white/50 font-black text-xs pb-4">:</span>
                  </>
                )}
                <CompactTimeBlock value={timeLeft.hours} label="سا" />
                <span className="text-white/50 font-black text-xs pb-4">:</span>
                <CompactTimeBlock value={timeLeft.minutes} label="دق" />
                <span className="text-white/50 font-black text-xs pb-4">:</span>
                <CompactTimeBlock value={timeLeft.seconds} label="ث" />
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Full version for detail page — NO repeat info shown to customer
  return (
    <div className="rounded-2xl overflow-hidden border-2 border-red-200 shadow-lg">
      <div className={`px-4 py-3 flex items-center justify-between ${isUrgent ? 'bg-gradient-to-r from-red-600 to-rose-700' : 'bg-gradient-to-r from-red-500 to-rose-600'}`}>
        <div className="flex items-center gap-2 text-white">
          <SafeIcon icon={isUrgent ? LuFlame : LuZap} className={`w-5 h-5 ${isUrgent ? 'animate-bounce' : ''}`} />
          <div>
            <span className="font-black text-sm block">عرض خاص — خصم {pct}%</span>
            {isUrgent && <span className="text-red-200 text-xs font-bold">⚡ ينتهي قريباً!</span>}
          </div>
        </div>
        <div className="text-right text-white/80 text-xs font-bold bg-white/10 px-2 py-1 rounded-lg">
          وفر {savings.toLocaleString()} دج
        </div>
      </div>

      <div className="bg-gradient-to-b from-red-50 to-white px-4 py-3 flex items-center justify-between border-b border-red-100">
        <div>
          <p className="text-xs text-slate-400 font-medium mb-0.5">السعر الأصلي</p>
          <p className="text-slate-400 line-through text-base font-bold">{originalPrice.toLocaleString()} دج</p>
        </div>
        <div className="text-right">
          <p className="text-xs text-red-500 font-bold mb-0.5">سعر التخفيض</p>
          <p className="text-red-600 font-black text-2xl leading-none">
            {discountPrice.toLocaleString()}
            <span className="text-sm font-bold"> دج</span>
          </p>
        </div>
      </div>

      {timeLeft && (
        <div className="bg-slate-900 px-4 py-4">
          <div className="flex items-center justify-center gap-1 mb-3">
            <SafeIcon icon={LuClock} className="w-3.5 h-3.5 text-red-400" />
            <p className="text-center text-xs font-black text-red-400 uppercase tracking-widest">
              {isUrgent ? '⚠️ ينتهي العرض خلال' : 'ينتهي العرض خلال'}
            </p>
          </div>
          <div className="flex items-end justify-center gap-2" dir="ltr">
            {timeLeft.days > 0 && (
              <>
                <TimeBlock value={timeLeft.days} label="يوم" pulse={isUrgent} />
                <Colon />
              </>
            )}
            <TimeBlock value={timeLeft.hours} label="ساعة" pulse={isUrgent} />
            <Colon />
            <TimeBlock value={timeLeft.minutes} label="دقيقة" pulse={isUrgent} />
            <Colon />
            <TimeBlock value={timeLeft.seconds} label="ثانية" pulse={isUrgent} />
          </div>
        </div>
      )}
    </div>
  );
};

export default DiscountTimer;